'use client'

import Link from 'next/link'
import { useState } from 'react'
import { Avatar, AvatarFallback, AvatarImage } from '~/components/ui/avatar'
import { AuthPromptDialog } from '~/components/auth/AuthPromptDialog'
import type { CoFacilitatorData } from '~/lib/facilitator-demo-data'
import {
  FONT_BODY,
  FONT_HEADING,
  FONT_UI,
  HAIRLINE,
  INK,
  INK_MUTED,
  MUTED,
  SAGE_TINT,
  SURFACE,
  TERRACOTTA,
} from '~/lib/brand'

/**
 * "Community collaborators" — the design's full-width row of collaborator
 * cards on the public facilitator profile (#1019).
 *
 * The data is the same derivation the page always used: every other host on
 * this facilitator's events, ranked by how many they co-hosted. Only the
 * presentation changed — a snap-scrolling row of 300px cards with the
 * design's section header, instead of a card-in-a-card with a pill count.
 *
 * `CoFacilitators` (the older component) stays for the hosting dashboard and
 * the admin facilitator page, which are not part of this redesign.
 *
 * Signed-out visitors are still gated the way they were: a click on a
 * collaborator opens the sign-up prompt rather than another public profile.
 */

export const MAX_COLLABORATOR_CARDS = 6

type Props = {
  collaborators: CoFacilitatorData[]
  seeAllHref: string
  isAuthenticated: boolean
}

export function FacilitatorCollaboratorRow({ collaborators, seeAllHref, isAuthenticated }: Props) {
  const visible = collaborators.slice(0, MAX_COLLABORATOR_CARDS)
  const [showAuthPrompt, setShowAuthPrompt] = useState(false)

  const gateClick = (e: React.MouseEvent) => {
    if (!isAuthenticated) {
      e.preventDefault()
      setShowAuthPrompt(true)
    }
  }

  return (
    <section aria-label="Community collaborators" data-testid="facilitator-collaborators">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div
          className="flex h-[34px] w-[34px] items-center justify-center rounded-[11px] text-[15px]"
          style={{ background: SAGE_TINT, color: '#3F6258' }}
          aria-hidden="true"
        >
          ⚇
        </div>
        <h2
          className="text-[18px] font-extrabold uppercase tracking-[.05em]"
          style={{ ...FONT_HEADING, color: INK }}
        >
          Community collaborators
        </h2>
        {collaborators.length > 0 && (
          <span className="text-[12.5px] italic" style={{ ...FONT_BODY, color: INK_MUTED }}>
            {collaborators.length} {collaborators.length === 1 ? 'collaborator' : 'collaborators'}
          </span>
        )}
        {collaborators.length > MAX_COLLABORATOR_CARDS && (
          <Link
            href={seeAllHref}
            onClick={gateClick}
            className="ml-auto text-[13px] font-bold transition-colors hover:text-[#96442C]"
            style={{ ...FONT_UI, color: TERRACOTTA }}
          >
            See all collaborators →
          </Link>
        )}
      </div>

      {visible.length === 0 ? (
        /* FR-10 (#946) — a defined empty state. */
        <div
          className="rounded-[18px] px-[26px] py-6"
          style={{ background: SURFACE, border: `1px solid ${HAIRLINE}` }}
        >
          <p className="text-sm" style={{ ...FONT_BODY, color: MUTED }}>
            No collaborators yet. Co-hosted events appear here once they happen.
          </p>
        </div>
      ) : (
        // The row scrolls sideways inside its own box; the page must not.
        // `min-w-0` on the page column is what lets that hold at 360px.
        <div className="-mx-1 flex snap-x snap-mandatory gap-3.5 overflow-x-auto px-1 pb-2">
          {visible.map((collaborator) => (
            <Link
              key={collaborator.user_id}
              href={
                collaborator.user_id.startsWith('demo-')
                  ? '#'
                  : `/facilitator-profile/${collaborator.user_id}`
              }
              onClick={gateClick}
              data-testid="facilitator-collaborator-card"
              className="flex w-[300px] max-w-[85vw] flex-shrink-0 snap-start items-center gap-[13px] rounded-[16px] px-[17px] py-[15px] transition-[border-color,box-shadow] hover:shadow-[0_8px_20px_rgba(44,45,43,.07)]"
              style={{ background: SURFACE, border: `1px solid ${HAIRLINE}` }}
            >
              <Avatar
                className="h-[46px] w-[46px] flex-shrink-0"
                style={{ boxShadow: `0 0 0 2px ${SURFACE}, 0 0 0 3.5px ${HAIRLINE}` }}
              >
                {collaborator.avatar_url && (
                  <AvatarImage src={collaborator.avatar_url} alt="" />
                )}
                <AvatarFallback className="bg-[#DDE7E5] text-sm font-bold text-[#3E5B53]">
                  {collaborator.full_name.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <div className="truncate text-[14px] font-bold" style={{ ...FONT_UI, color: INK }}>
                  {collaborator.full_name}
                </div>
                <div
                  className="mt-0.5 line-clamp-2 text-[11.5px] leading-[1.35]"
                  style={{ ...FONT_BODY, color: MUTED }}
                >
                  {collaborator.role}
                </div>
                <div className="mt-[3px] text-[11.5px]" style={{ ...FONT_BODY, color: INK_MUTED }}>
                  Co-hosted {collaborator.cohosted_count}{' '}
                  {collaborator.cohosted_count === 1 ? 'event' : 'events'}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      <AuthPromptDialog
        open={showAuthPrompt}
        onOpenChange={setShowAuthPrompt}
        title="View facilitator profiles"
        description="Create your free account to explore the full community of facilitators."
        icon="👥"
      />
    </section>
  )
}
